import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { TextField, Dialog, DialogContent, DialogTitle, MenuItem, InputAdornment, Grid2, Box, Typography, useTheme, Card, CardContent, Chip, Tooltip } from '@mui/material';
import { Search, Event, LocationOn, AccessTime, Category, Notes } from '@mui/icons-material';
import { getEventsActivities } from '../actions/activities';
import { useLocation } from 'react-router-dom';
import { cardGridSx, cardItemSx } from '../utils/cardLayout';
import { format } from 'date-fns-tz';

const localizer = momentLocalizer(moment);

const MultiViewCalendarIntegration = () => {
  const theme = useTheme();
  const location = useLocation();
  
  const eventIds = useMemo(() => {
    const queryParam = new URLSearchParams(location.search).get('eventIds');
    return queryParam ? queryParam.split(',') : [];
  }, [location.search]);

  const [activities, setActivities] = useState([]);
  const [currentActivity, setCurrentActivity] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  
  // Get user's time zone
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Fetch activities for the specific event on load
  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchedActivities = await getEventsActivities(eventIds);

        const formattedActivities = fetchedActivities.map(activity => {        
          const startDate = new Date(activity.start);
          const endDate = new Date(activity.end);
        
          return {
            ...activity, // You may need to remove toJSON if it's not a model instance
            start: startDate,
            end: endDate,
          };
        });
        
        setActivities(formattedActivities);
      } catch (error) {
        console.error('Failed to fetch activities', error);
      }
    };
    fetchData();
  }, [eventIds, timeZone]); // Include timeZone as a dependency

  // Open dialog for creating/editing
  const handleOpenDialog = (activity = {}) => {
    setCurrentActivity(activity);
    setIsEditing(!!activity.id);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setCurrentActivity({});
    setOpenDialog(false);
  };

  const filteredActivities = activities.filter((activity) => {
    const matchesSearch = activity.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTag = filterTag ? activity.tags.includes(filterTag) : true;
    return matchesSearch && matchesTag;
  });

  const uniqueTags = Array.from(new Set(activities.flatMap(activity => activity.tags)));

  const formatDateTime = (date) => {
    if (!date) return 'Not set';
    return format(date, 'MMM dd, yyyy - HH:mm', { timeZone });
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Filter Section */}
      <Box sx={{ mb: 4, mt: { xs: 4, sm: 0 } }}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6, md: 5 }}>
            <TextField 
              label="Filter by Tag" 
              select 
              value={filterTag} 
              onChange={(e) => setFilterTag(e.target.value)} 
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            >
              <MenuItem value="">All Tags</MenuItem>
              {uniqueTags.map(tag => (
                <MenuItem key={tag} value={tag}>{tag}</MenuItem>
              ))}
            </TextField>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 5 }}>
            <TextField 
              label="Search Activity" 
              value={searchQuery} 
              onChange={(e) => setSearchQuery(e.target.value)} 
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }
              }}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Grid2>
        </Grid2>
        
        {/* Empty State */}
        {filteredActivities.length === 0 && (
          <Card sx={{ 
            p: { xs: 3, sm: 4 }, 
            textAlign: 'center',
            mt: 3,
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 20px rgba(0, 0, 0, 0.2)'
              : '0 4px 20px rgba(0, 0, 0, 0.1)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(255, 255, 255, 0.2)',
          }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              {(searchQuery || filterTag) ? 
                'No activities found for the selected filters' : 
                'No activities found for the selected events'}
            </Typography>
          </Card>
        )}
      </Box>

      {/* Calendar Container */}
      <Card sx={{
        background: theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
        borderRadius: 3,
        boxShadow: theme.palette.mode === 'dark'
          ? '0 4px 20px rgba(0, 0, 0, 0.2)'
          : '0 4px 20px rgba(0, 0, 0, 0.1)',
        border: theme.palette.mode === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.1)'
          : '1px solid rgba(255, 255, 255, 0.2)',
        p: { xs: 1, sm: 2 },
        overflow: 'hidden',
        mb: 4
      }}>
        <Calendar
          localizer={localizer}
          events={filteredActivities}
          startAccessor="start"
          endAccessor="end"
          style={{ 
            height: 500,
            fontFamily: theme.typography.fontFamily
          }}
          onSelectEvent={handleOpenDialog}
        />
      </Card>

      {/* Activities List */}
      {filteredActivities.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            All Activities
          </Typography>
          <Box sx={cardGridSx()}>
            {filteredActivities.map((activity, index) => (
              <Box key={activity.id || index} sx={cardItemSx()}>
                <Card sx={{
                  height: '100%',
                  borderRadius: 3,
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                    : '0 4px 20px rgba(0, 0, 0, 0.1)',
                  border: theme.palette.mode === 'dark'
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(255, 255, 255, 0.2)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 8px 30px rgba(0, 0, 0, 0.3)'
                      : '0 8px 30px rgba(0, 0, 0, 0.15)',
                  }
                }}
                onClick={() => handleOpenDialog(activity)}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, minHeight: '1.5rem' }}>
                      {activity.title}
                    </Typography>

                    {/* Event Badge */}
                    {activity.event_title && (
                      <Box sx={{ mb: 2 }}>
                        <Chip
                          label={activity.event_title}
                          size="small"
                          sx={{
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            height: { xs: 20, sm: 24 },
                            backgroundColor: theme.palette.secondary.main + '20',
                            color: theme.palette.secondary.main,
                            fontWeight: 500,
                            '& .MuiChip-label': {
                              px: { xs: 1, sm: 1.5 }
                            }
                          }}
                        />
                      </Box>
                    )}

                    {/* Activity Details Section */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AccessTime sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                          {formatDateTime(activity.start)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AccessTime sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.secondary.main }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                          {formatDateTime(activity.end)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <LocationOn sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                        <Typography variant="body2" color="text.secondary" sx={{ 
                          fontSize: { xs: '0.8rem', sm: '0.875rem' },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {activity.location || 'No location'}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Tags Section */}
                    {activity.tags && activity.tags.length > 0 && (
                      <Box sx={{ mb: 2, minHeight: '2rem' }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {activity.tags.slice(0, 3).map((tag, tagIndex) => (
                            <Chip
                              key={tagIndex}
                              label={tag}
                              size="small"
                              sx={{
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                height: { xs: 20, sm: 24 },
                                backgroundColor: theme.palette.primary.main + '20',
                                color: theme.palette.primary.main,
                                '& .MuiChip-label': {
                                  px: { xs: 1, sm: 1.5 }
                                }
                              }}
                            />
                          ))}
                          {activity.tags.length > 3 && (
                            <Chip
                              label={`+${activity.tags.length - 3}`}
                              size="small"
                              sx={{
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                height: { xs: 20, sm: 24 },
                                backgroundColor: theme.palette.grey[300],
                                color: theme.palette.text.secondary,
                                '& .MuiChip-label': {
                                  px: { xs: 1, sm: 1.5 }
                                }
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Description Section - Fixed height */}
                    <Box sx={{ mb: 2, minHeight: '2.5rem' }}>
                      {activity.description && (
                        <Tooltip 
                          title={activity.description} 
                          arrow 
                          placement="top"
                          sx={{ 
                            '& .MuiTooltip-tooltip': {
                              maxWidth: 300,
                              fontSize: '0.875rem',
                              lineHeight: 1.4
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', cursor: 'help' }}>
                            <Notes sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, mt: 0.2, color: theme.palette.primary.main }} />
                            <Typography variant="body2" color="text.secondary" sx={{ 
                              fontSize: { xs: '0.8rem', sm: '0.875rem' }, 
                              fontStyle: 'italic',
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}>
                              {activity.description}
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                    </Box>

                    {/* View Details Hint */}
                    <Box sx={{ 
                      mt: 'auto',
                      pt: 2,
                      borderTop: `1px solid ${theme.palette.divider}`,
                      textAlign: 'center'
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.8rem' },
                        fontStyle: 'italic'
                      }}>
                        Click to view details
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Activity Dialog - Read Only */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogTitle sx={{ 
          fontWeight: 600,
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
        }}>
          Activity Details
        </DialogTitle>
        <DialogContent sx={{ 
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.02)'
            : 'rgba(255, 255, 255, 0.9)',
          pt: 2
        }}>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField 
                label="Activity Title" 
                name="title" 
                value={currentActivity.title || ''} 
                fullWidth 
                margin="normal"
                slotProps={{
                  input: {
                    readOnly: true,
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField 
                label="Location" 
                name="location" 
                value={currentActivity.location || ''} 
                fullWidth 
                margin="normal"
                slotProps={{
                  input: {
                    readOnly: true,
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
            {currentActivity.event_title && (
              <Grid2 size={{ xs: 12 }}>
                <TextField 
                  label="Event" 
                  name="event" 
                  value={currentActivity.event_title || ''} 
                  fullWidth 
                  margin="normal"
                  slotProps={{
                    input: {
                      readOnly: true,
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid2>
            )}
            <Grid2 size={{ xs: 12 }}>
              <TextField 
                label="Description" 
                name="description" 
                value={currentActivity.description || ''} 
                fullWidth 
                margin="normal"
                multiline
                rows={3}
                slotProps={{
                  input: {
                    readOnly: true,
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
              <TextField 
                label="Tags" 
                name="tags" 
                value={currentActivity.tags ? currentActivity.tags.join(', ') : ''} 
                fullWidth 
                margin="normal"
                slotProps={{
                  input: {
                    readOnly: true,
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField 
                label="Start Time" 
                type="datetime-local" 
                name="start" 
                value={currentActivity.start ? format(currentActivity.start, 'yyyy-MM-dd\'T\'HH:mm', { timeZone }) : ''} 
                fullWidth 
                margin="normal" 
                slotProps={{ 
                  inputLabel: { shrink: true },
                  input: {
                    readOnly: true,
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField 
                label="End Time" 
                type="datetime-local" 
                name="end" 
                value={currentActivity.end ? format(currentActivity.end, 'yyyy-MM-dd\'T\'HH:mm', { timeZone }) : ''} 
                fullWidth 
                margin="normal" 
                slotProps={{ 
                  inputLabel: { shrink: true },
                  input: {
                    readOnly: true,
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
          </Grid2>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MultiViewCalendarIntegration;
